import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { Switch } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";
import { z } from "zod";

import { createAxiosInstance } from "api/axiosConfig";
import { FormField } from "components/form/FormField/FormField";
import { Checkbox } from "components/ui/Checkbox";
import { DataLoadError } from "components/ui/DataLoadError";
import { Select } from "components/ui/Select/Select";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useDrugAllergies } from "features/patients/hooks/useDrugAllergies";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { toDatePickerValue } from "utils/formDateValue";
import { deriveFinishDate } from "utils/medicationDates";
import { showMutationError } from "utils/showMutationError";

import { SelectDrug } from "../AddPrescriptionForm/SelectDrug/SelectDrug";
import style from "./AddMedicationForm.module.css";

const medicineSchema = z.object({
  dosage: z.string().optional(),
  form: z.string().optional(),
  startDate: z.string().optional(),
  finishDate: z.string().optional(),
  duration: z.string().optional(),
  durationFormat: z.string().optional(),
  usageType: z.string().optional(),
  repeat: z.string().optional(),
  route: z.string().optional(),
  frequency: z.string().optional(),
  quantity: z.string().optional(),
  isManual: z.boolean().optional(),
  tradeName: z.string().optional(),
  manufacturer: z.string().optional(),
  packSize: z.string().optional(),
  medicineId: z.union([z.string(), z.number()]).optional(),
  note: z.string().optional(),
});

const formSchema = z.object({
  medicine: medicineSchema,
});

export const AddMedicationForm = ({
  onSuccess,
  onCancel,
  initialData = {},
  usageType = "LONGTERM",
}) => {
  const [hasFinishDate, setHasFinishDate] = useState(false);
  // Drugs whose allergy check could not be completed, and the prescriber's
  // acknowledgement of that (KIN-1615).
  const [failedAllergyChecks, setFailedAllergyChecks] = useState({});
  const [
    hasAcknowledgedUncheckedAllergies,
    setHasAcknowledgedUncheckedAllergies,
  ] = useState(false);

  const queryClient = useQueryClient();
  const today = dayjs(new Date().setMinutes(0));
  const patientId = useCurrentPatientId();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");
  const { data: drugAllergies, isError: isDrugAllergiesError } =
    useDrugAllergies(patientId);
  const { data: practiceSettings } = usePracticeSettings();
  const notesEnabled = !!practiceSettings?.medicationNotesEnabled;

  // Handler for when a drug is selected and checked for allergies. Only the
  // completeness of the check is tracked here — AddMedicationForm already
  // shows the patient's full allergy list up front (below), so there is no
  // separate per-selection allergy banner to populate, unlike the
  // multi-medicine prescription form.
  // `checkState.didFail` distinguishes "checked, none found" from "could not
  // check" — both arrive here with an empty allergies list (KIN-1615).
  const handleAllergyCheck = (index, drugId, allergies, checkState = {}) => {
    setFailedAllergyChecks((prev) => {
      const updated = { ...prev };
      if (checkState.didFail) {
        updated[index] = {
          drugName: checkState.drug?.tradeName ?? "this medication",
          retry: checkState.retry,
        };
      } else {
        delete updated[index];
      }
      return updated;
    });
  };

  const uncheckedDrugNames = Object.values(failedAllergyChecks).map(
    (entry) => entry.drugName
  );
  const retryFailedAllergyChecks = () =>
    Object.values(failedAllergyChecks).forEach((entry) => entry.retry?.());
  // Fail-open is the dangerous default on this screen, so an incomplete check
  // has to be acknowledged before the medication can be saved.
  const needsAllergyAcknowledgement =
    uncheckedDrugNames.length > 0 || isDrugAllergiesError;
  const isBlockedOnAllergyCheck =
    needsAllergyAcknowledgement && !hasAcknowledgedUncheckedAllergies;

  const getDefaultMedicine = () => ({
    dosage: "As prescribed",
    form: "Tablet",
    startDate: moment(new Date(today)).format("DD-MM-YYYY"),
    // Derived from the duration, and empty until there is one — never today, which stored a course that
    // finished on the day it was issued and now reads as discontinued (KIN-1932).
    finishDate: "",
    duration: "",
    durationFormat: "days",
    quantity: "",
    usageType: usageType,
    repeat: "",
    route: "",
    frequency: "",
    isManual: false,
    tradeName: "",
    manufacturer: "",
    packSize: "",
    medicineId: "",
    note: "",
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      medicine: initialData.medicine || getDefaultMedicine(),
    },
  });

  // Set once the prescriber types a finish date themselves: an explicit clinical decision stops the
  // field tracking the duration (KIN-1932).
  const hasManuallySetFinishDate = useRef(false);

  const watchedStartDate = form.watch("medicine.startDate");
  const watchedDuration = form.watch("medicine.duration");
  const watchedDurationFormat = form.watch("medicine.durationFormat");
  const watchedFinishDate = form.watch("medicine.finishDate");

  useEffect(() => {
    if (hasManuallySetFinishDate.current) {
      return;
    }
    const derived = deriveFinishDate({
      startDate: watchedStartDate,
      duration: watchedDuration,
      durationFormat: watchedDurationFormat,
    });
    if (derived !== form.getValues("medicine.finishDate")) {
      form.setValue("medicine.finishDate", derived);
    }
  }, [watchedStartDate, watchedDuration, watchedDurationFormat, form]);

  const createMedicationMutation = useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (medicationData) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/patientmedication`,
        medicationData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Medication added successfully");
      queryClient.invalidateQueries({ queryKey: ["longTermMedications"] });
      queryClient.invalidateQueries({ queryKey: ["acuteMedications"] });
      queryClient.invalidateQueries({
        queryKey: ["paginatedLongTermMedications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["paginatedAcuteMedications"],
      });
      onSuccess?.();
    },
    onError: (error) => showMutationError(error, "Failed to add medication"),
  });

  const handleSubmit = async () => {
    if (isBlockedOnAllergyCheck) return;

    const values = form.getValues();
    const medicine = values.medicine;

    const isValid = await form.trigger();
    if (!isValid) {
      console.error("Form validation failed:", form.formState.errors);
      return;
    }

    // Check if medication is selected
    if (!medicine.isManual && !medicine.medicineId) {
      toast.error("Please select a medication");
      return;
    }

    if (medicine.isManual && !medicine.tradeName) {
      toast.error("Please enter a trade name for manual medication");
      return;
    }

    const medicationData = {
      patientId: patientId,
      dosage: medicine.dosage,
      form: medicine.form,
      startDate: moment(medicine.startDate, "DD-MM-YYYY").format("YYYY-MM-DD"),
      finishDate: hasFinishDate
        ? moment(medicine.finishDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : null,
      route: medicine.route,
      duration: medicine.duration ? Number(medicine.duration) : null,
      durationFormat: medicine.durationFormat,
      frequency: medicine.frequency,
      acuteLongTerm: medicine.usageType,
      repeat: medicine.repeat ? Number(medicine.repeat) : null,
      quantity: medicine.quantity ? Number(medicine.quantity) : null,
      note: notesEnabled ? medicine.note : undefined,
    };

    if (medicine.isManual) {
      medicationData.tradeName = medicine.tradeName;
      medicationData.manufacturer = medicine.manufacturer;
      medicationData.packSize = medicine.packSize;
    } else {
      medicationData.medicationId = medicine.medicineId;
    }

    createMedicationMutation.mutate(medicationData);
  };

  return (
    <FormProvider {...form}>
      {drugAllergies && drugAllergies.length > 0 && (
        <div className={style.allergyWarningSection}>
          <div className={style.allergyWarningHeader}>
            <span className={style.warningIcon}>⚠️</span>
            <span className={style.allergyWarningTitle}>Drug Allergies:</span>
          </div>
          <ul className={style.allergyList}>
            {drugAllergies.map((allergy) => (
              <li key={allergy.id} className={style.allergyItem}>
                <strong>{allergy.drugName || allergy.name}</strong>
                {allergy.drugGenericName && (
                  <span> ({allergy.drugGenericName})</span>
                )}
                <span> - {allergy.allergyReaction}</span>
                {allergy.serious && (
                  <span className={style.seriousFlag}> - SERIOUS</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form
        className={style.addMedicationForm}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className={style.medicineSection}>
          <div className={style.drugSection}>
            <SelectDrug
              index={0}
              form={{
                ...form,
                watch: (name) =>
                  form.watch(name.replace("medicines.0", "medicine")),
                setValue: (name, value) =>
                  form.setValue(name.replace("medicines.0", "medicine"), value),
                register: (name, options) =>
                  form.register(
                    name.replace("medicines.0", "medicine"),
                    options
                  ),
              }}
              initialData={initialData.medicine}
              onAllergyCheck={handleAllergyCheck}
            />
          </div>

          <div className={style.inputGroupGrid}>
            <FormField name="medicine.dosage">
              <label className={style.formLabel}>Dosage</label>
              <input
                className="form-control"
                {...form.register("medicine.dosage")}
              />
            </FormField>

            <FormField
              name="medicine.form"
              error={form.formState.errors.medicine?.form}
            >
              <label className={style.formLabel}>Form</label>
              <input
                className="form-control"
                {...form.register("medicine.form")}
              />
            </FormField>

            <div>
              <FormField name="medicine.startDate">
                <label className={style.formLabel}>Start date</label>
                <div className={style.datePickerWrapper}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      defaultValue={today}
                      sx={{ width: "100%", height: "36px" }}
                      onChange={(newValue) => {
                        form.setValue(
                          "medicine.startDate",
                          moment(new Date(dayjs(newValue))).format("DD-MM-YYYY")
                        );
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </FormField>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "20px",
                }}
              >
                <Switch
                  checked={hasFinishDate}
                  onChange={() => setHasFinishDate((prev) => !prev)}
                  sx={{
                    padding: "6px",
                    marginLeft: "-6px",
                    borderRadius: "16px",
                    "& .MuiSwitch-switchBase": {
                      "&.Mui-checked": {
                        color: "#fff",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "var(--green-800)",
                          opacity: 1,
                        },
                      },
                    },
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#fff",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#a0a0a0",
                      opacity: 0.5,
                      padding: 0,
                      borderRadius: "16px",
                    },
                  }}
                />
                <span style={{ fontSize: "15px", marginTop: "2px" }}>
                  {hasFinishDate ? "Finish day" : "Add finish day"}
                </span>
              </div>
            </div>

            {hasFinishDate ? (
              <FormField name="medicine.finishDate">
                <label className={style.formLabel}>Finish date</label>
                <div className={style.datePickerWrapper}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      // Controlled, so the date derived from the duration is what the prescriber sees —
                      // an uncontrolled picker showed today while the form held something else (KIN-1932).
                      value={toDatePickerValue(watchedFinishDate)}
                      sx={{ width: "100%", height: "36px" }}
                      onChange={(newValue) => {
                        hasManuallySetFinishDate.current = true;
                        form.setValue(
                          "medicine.finishDate",
                          moment(new Date(dayjs(newValue))).format("DD-MM-YYYY")
                        );
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </FormField>
            ) : (
              <div />
            )}

            <FormField name="medicine.route">
              <label className={style.formLabel}>Route</label>
              <Select
                name="medicine.route"
                options={[
                  { label: "Aural", value: "Aural" },
                  { label: "Inhalation", value: "Inh" },
                  { label: "Intramuscular", value: "IM" },
                  { label: "Intranasal", value: "Intranasal" },
                  { label: "Intravenous", value: "IV" },
                  { label: "PO", value: "PO" },
                  { label: "PR", value: "PR" },
                  { label: "PV", value: "PV" },
                  { label: "Subcutaneous", value: "SC" },
                  { label: "Sublingual", value: "Sublingual" },
                  { label: "Topical", value: "Top" },
                ]}
                value={form.watch("medicine.route")}
                onChange={(value) => form.setValue("medicine.route", value)}
                fullWidth
              />
            </FormField>

            <FormField name="medicine.duration">
              <label className={style.formLabel}>Duration (optional)</label>
              <input
                className="form-control"
                inputMode="text"
                pattern="[0-9]*"
                maxLength={4}
                {...form.register("medicine.duration", {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  },
                })}
              />
            </FormField>

            <FormField name="medicine.durationFormat">
              <label className={style.formLabel}>
                Duration format (optional)
              </label>
              <Select
                name="medicine.durationFormat"
                options={[
                  { label: "Days", value: "days" },
                  { label: "Months", value: "months" },
                ]}
                value={form.watch("medicine.durationFormat")}
                onChange={(value) =>
                  form.setValue("medicine.durationFormat", value)
                }
                placeholder={null}
                fullWidth
              />
            </FormField>

            <FormField name="medicine.frequency">
              <label className={style.formLabel}>Frequency</label>
              <Select
                name="medicine.frequency"
                options={[
                  { label: "STAT", value: "STAT" },
                  { label: "OD - Once Daily", value: "OD" },
                  { label: "BD - Twice Daily", value: "BD" },
                  { label: "Three times daily", value: "TDS" },
                  { label: "Four times daily", value: "QDS" },
                  { label: "As required", value: "PRN" },
                  { label: "Once Weekly", value: "Once Weekly" },
                  { label: "Twice Weekly", value: "Twice Weekly" },
                  { label: "Three time weekly", value: "Three time weekly" },
                ]}
                value={form.watch("medicine.frequency")}
                onChange={(value) => form.setValue("medicine.frequency", value)}
                fullWidth
              />
            </FormField>

            <FormField name="medicine.quantity">
              <label className={style.formLabel}>Quantity (optional)</label>
              <input
                className="form-control"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                {...form.register("medicine.quantity", {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  },
                })}
              />
            </FormField>

            <FormField name="medicine.usageType">
              <label className={style.formLabel}>Usage type</label>
              <Select
                name="medicine.usageType"
                options={[
                  { label: "Acute", value: "ACUTE" },
                  { label: "Long term", value: "LONGTERM" },
                ]}
                value={form.watch("medicine.usageType")}
                onChange={(value) => form.setValue("medicine.usageType", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>

            {form.watch("medicine.usageType") === "LONGTERM" && (
              <FormField name="medicine.repeat">
                <label className={style.formLabel}>Repeat (optional)</label>
                <input
                  className="form-control"
                  placeholder="How many times?"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  {...form.register("medicine.repeat", {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    },
                  })}
                />
                <p className={style.helperText}>
                  Number of times this prescription can be reissued without a
                  new consultation.
                </p>
              </FormField>
            )}

            {notesEnabled && (
              <div className={style.noteField}>
                <FormField name="medicine.note">
                  <label className={style.formLabel}>Note (optional)</label>
                  <textarea
                    className="form-control textareaform-control"
                    rows={3}
                    {...form.register("medicine.note")}
                  />
                </FormField>
              </div>
            )}
          </div>
        </div>

        {uncheckedDrugNames.length > 0 && (
          <DataLoadError
            label={`the allergy check for ${uncheckedDrugNames.join(", ")}`}
            text="No allergy was found because the check did not complete, not because the patient has none."
            onRetry={retryFailedAllergyChecks}
          />
        )}
        {needsAllergyAcknowledgement && (
          <label className={style.allergyAcknowledgement}>
            <Checkbox
              checked={hasAcknowledgedUncheckedAllergies}
              handleChange={(e) =>
                setHasAcknowledgedUncheckedAllergies(e.target.checked)
              }
            />
            I understand the allergy check could not be completed and have
            verified this patient's allergies another way.
          </label>
        )}

        <div className={style.bottomSection}>
          <div className={style.buttonGroup}>
            {onCancel && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCancel?.();
                }}
                className={style.cancelButton}
                type="button"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                createMedicationMutation.isPending || isBlockedOnAllergyCheck
              }
              className={style.saveButton}
            >
              {createMedicationMutation.isPending
                ? "Saving..."
                : "Save Medication"}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
